/**
 * Book payload normalization
 *
 * Builds the bookingData object sent to cancellara's /book endpoint.
 * The shape differs between flow types:
 *
 * ── Flight-and-stay (Alpitour) ─────────────────────────────────────
 *   Stateful backend: the session `id` is enough — the backend already
 *   knows the solution, dates, flights, etc.
 *   { id, compositions, payments, live_price, bookToken }
 *
 * ── Only-stay (Sololand) ──────────────────────────────────────────
 *   Stateless API: all booking identifiers must be sent explicitly.
 *   { idSolution, roomRef, dateFrom, dateTo, nights,
 *     compositions, payments, live_price, bookToken }
 */

/**
 * Build the bookingData for a flight-and-stay (Alpitour) booking.
 *
 * @param {Object} params
 * @param {Object} params.quote     - The quote response stored in Vuex
 * @param {Array}  params.paxs      - Processed passenger array
 * @param {Object} params.payments  - Payment data from sidebar
 * @returns {Object} bookingData
 */
const build_flight_and_stay_booking_data = ({ quote, paxs, payments }) => ({
  payments,
  id: quote.id,
  compositions: { passenger: paxs },
  live_price: quote.live_price,
  bookToken: quote.bookToken,
});

/**
 * Build the bookingData for an only-stay (Sololand) booking.
 *
 * @param {Object} params
 * @param {Object} params.quote          - The quote/check response stored in Vuex
 * @param {Array}  params.paxs           - Processed passenger array
 * @param {Object} params.payments       - Payment data from sidebar
 * @param {Object} params.selected_room  - The selected_room from Vuex (contains .solution)
 * @returns {Object} bookingData
 */
const build_only_stay_booking_data = ({ quote, paxs, payments, selected_room }) => {
  const { solution = {} } = selected_room || {};

  const data = {
    payments,
    idSolution: solution.idSolution,
    roomRef: solution.roomRef,
    dateFrom: solution.dateFrom,
    dateTo: solution.dateTo,
    nights: solution.nights,
    compositions: { passenger: paxs },
    live_price: quote.live_price,
  };

  if (quote.bookToken) { data.bookToken = quote.bookToken; }
  if (quote.sololand_book_token) { data.sololand_book_token = quote.sololand_book_token; }

  return data;
};

/**
 * Build the bookingData based on the active package type.
 *
 * @param {Object} params
 * @param {boolean} params.is_only_stay  - Whether this is an only-stay package
 * @param {Object}  params.quote         - The quote/check response
 * @param {Array}   params.paxs          - Processed passenger array
 * @param {Object}  params.payments      - Payment data from sidebar
 * @param {Object}  params.selected_room - The selected_room from Vuex
 * @returns {Object} bookingData ready for the /book endpoint
 */
export const build_booking_data = ({ is_only_stay, quote, paxs, payments, selected_room }) => {
  if (is_only_stay) {
    return build_only_stay_booking_data({ quote, paxs, payments, selected_room });
  }
  return build_flight_and_stay_booking_data({ quote, paxs, payments });
};
